import { MEASUREMENT_PANEL_CORNER_ANGLE_THRESHOLD } from "../settings/altitude-view-settings";
/**
 * Detect corners (structural boundaries) in a polyline defined by point coordinates.
 * A corner is detected when the angle between adjacent segments exceeds the threshold.
 * Always includes the first and last indices as endpoints.
 * @param points Array of {x, y} coordinates
 * @returns Array of indices that are corners (sorted ascending)
 */
export function detectCorners(points) {
    const corners = [];
    if (points.length < 3) {
        return [];
    }
    // Always include left endpoint (index 0)
    corners.push(0);
    // Check intermediate points for angle changes
    for (let i = 1; i < points.length - 1; i++) {
        const p0 = points[i - 1];
        const p1 = points[i];
        const p2 = points[i + 1];
        // Calculate vectors
        const v1x = p1.x - p0.x;
        const v1y = p1.y - p0.y;
        const v2x = p2.x - p1.x;
        const v2y = p2.y - p1.y;
        // Calculate angle between vectors in degrees
        const angle1 = Math.atan2(v1y, v1x);
        const angle2 = Math.atan2(v2y, v2x);
        let angleDiff = Math.abs(angle2 - angle1) * (180 / Math.PI);
        // Normalize to [0, 180]
        if (angleDiff > 180) {
            angleDiff = 360 - angleDiff;
        }
        if (angleDiff >= MEASUREMENT_PANEL_CORNER_ANGLE_THRESHOLD) {
            corners.push(i);
        }
    }
    // Always include right endpoint (last index)
    corners.push(points.length - 1);
    return corners;
}
/**
 * Determine the slope display format based on auto-detection.
 * - Slope absolute value <= 10% → percent format (e.g. "5.00%")
 * - Slope absolute value > 10% → ratio format (e.g. "0.50:1.00")
 * @param dy Vertical distance (y2 - y1)
 * @param dx Horizontal distance (x2 - x1)
 * @returns SlopeFormat object, or null if dx is 0 (vertical segment)
 */
export function determineSlopeFormat(dy, dx) {
    if (dx === 0)
        return null;
    // Round to avoid floating-point boundary issues (e.g. 10.000000000000002 → 10)
    const slopePct = Math.round(Math.abs((dy / dx) * 100) * 1e6) / 1e6;
    if (slopePct <= 10) {
        // Percent format: uses absolute value for consistency with ratio format
        const text = Math.abs((dy / dx) * 100).toFixed(3) + "%";
        return { type: "percent", text };
    }
    // Ratio format: uses absolute values to avoid double negatives
    const text = Math.abs(dy).toFixed(3) + ":" + Math.abs(dx).toFixed(3);
    return { type: "ratio", text };
}
//# sourceMappingURL=section-view-utils.js.map